import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import TopNavigation from '../../../components/TopNavigation.jsx'
import LoadingSpinner from '../../components/ui/LoadingSpinner'

const PhoneVerifyPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user, refreshUser } = useAuth()

  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [resending, setResending] = useState(false)
  const [message, setMessage] = useState('Enter the 6-digit code we sent to your phone.')
  const [errorText, setErrorText] = useState('')
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    const urlPhone = searchParams.get('phone') || ''
    if (urlPhone && !phone) setPhone(urlPhone)
  }, [searchParams, phone])

  const handleVerify = async () => {
    try {
      setErrorText('')
      setVerifying(true)
      if (!phone) {
        setErrorText('Please enter your phone number.')
        setVerifying(false)
        return
      }
      if (!code || code.length !== 6) {
        setErrorText('Please enter the 6-digit code.')
        setVerifying(false)
        return
      }

      // Verify the SMS OTP code
      const { data, error } = await supabase.auth.verifyOtp({
        phone: phone.trim(),
        token: code.trim(),
        type: 'sms'
      })

      if (error) throw error

      // Update phone_verified status in secret_users table
      if (data?.user) {
        const { error: updateError } = await supabase
          .from('secret_users')
          .update({ phone_verified: true })
          .eq('id', data.user.id)

        if (updateError) {
          console.error('Error updating phone_verified status:', updateError)
        }
      }

      setSuccess(true)

      // Check if we have an active session after verification
      const { data: { session } } = await supabase.auth.getSession()

      if (session?.user) {
        // Session exists - user is logged in
        setMessage('Phone verified successfully! Redirecting to dashboard...')

        // Wait for database trigger to propagate the phone_verified update
        await new Promise(resolve => setTimeout(resolve, 500))

        // Retry refreshUser up to 3 times to handle RLS propagation delays
        let retries = 3
        while (retries > 0) {
          try {
            await refreshUser()
            // If refreshUser succeeds without throwing, break out
            break
          } catch (err) {
            console.log(`[PhoneVerifyPage] refreshUser attempt ${4 - retries} failed, retrying...`)
            retries--
            if (retries > 0) {
              await new Promise(resolve => setTimeout(resolve, 500))
            }
          }
        }

        // Navigate to dashboard after ensuring user data is refreshed
        setTimeout(() => navigate('/dashboard', { replace: true }), 500)
      } else {
        // No session - redirect to login with mobile tab selected
        setMessage('Phone verified successfully! Redirecting to login...')
        setTimeout(() => navigate('/login?t=mobile', {
          state: { message: 'Phone verified! You can now sign in with your phone number.' }
        }), 1500)
      }
    } catch (err: any) {
      console.error('[PhoneVerifyPage] Verification error:', err)
      setErrorText(err?.message || 'Invalid or expired code. Request a new one and try again.')
    } finally {
      setVerifying(false)
    }
  }

  const handleResend = async () => {
    try {
      setErrorText('')
      setResending(true)
      if (!phone) {
        setErrorText('Please enter your phone number to resend the code.')
        setResending(false)
        return
      }

      // Resend SMS OTP
      const { error } = await supabase.auth.signInWithOtp({
        phone: phone.trim()
      })

      if (error) throw error

      setMessage('New verification code sent. Check your phone and enter the code above.')
    } catch (err: any) {
      setErrorText(err?.message || 'Failed to resend code. Try again in a moment.')
    } finally {
      setResending(false)
    }
  }

  return (
    <div className="min-h-screen-safe bg-background flex flex-col">
      <TopNavigation />

      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md text-center">
          {success ? (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground">Verification Successful</h2>
              <p className="text-muted-foreground">{message}</p>
            </div>
          ) : (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-foreground">Verify your phone</h2>
              <p className="text-muted-foreground">{message}</p>

              <div className="space-y-2 text-left">
                <label className="block text-sm font-medium text-foreground">Phone Number</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 555-123-4567"
                  className="w-full px-3 py-2 bg-transparent border border-border text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div className="space-y-2 text-left">
                <label className="block text-sm font-medium text-foreground">Verification Code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="w-full px-3 py-2 bg-transparent border border-border text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-center text-lg font-mono"
                />
              </div>

              {errorText && (
                <p className="text-xs text-red-500">{errorText}</p>
              )}

              <button
                onClick={handleVerify}
                disabled={verifying || !phone || code.length !== 6}
                className="bg-primary text-primary-foreground hover:bg-primary/90 w-full px-4 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-60"
              >
                {verifying ? <span className="inline-flex items-center"><LoadingSpinner size="sm" /> Verifying…</span> : 'Verify Phone'}
              </button>

              <button
                onClick={handleResend}
                disabled={resending || !phone}
                className="border border-border text-foreground hover:bg-muted w-full px-4 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-60"
              >
                {resending ? 'Sending…' : 'Send New Code'}
              </button>

              <div className="pt-2 text-sm">
                <button onClick={() => navigate('/login')} className="text-muted-foreground hover:text-foreground underline">
                  Back to Sign In
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PhoneVerifyPage